import { useState } from "react";
import { Wrench, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useLoad } from "@/hooks/useData";

const List = ({ items }) => (items.length ? items.map((c, i) => <div key={i}>{c.sku} · {c.name} ×{c.qty}</div>) : <span className="text-muted-foreground">—</span>);
const LABEL = { ok: ["Already correct", "text-emerald-700"], fix: ["Will be corrected", "text-amber-700"], add: ["Will be added", "text-sky-700"] };

const RepairTable = ({ rows }) => (
  <div className="max-h-[45vh] overflow-auto rounded border border-border">
    <table className="data-table text-xs" data-testid="asin-repair-table">
      <thead><tr><th>ASIN</th><th>Seller SKU</th><th className="min-w-[240px]">Now</th><th className="min-w-[240px]">Will be</th><th>Result</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.asin} data-testid={`asin-repair-row-${r.asin}`}>
          <td className="font-mono">{r.asin}</td><td className="wrap-cell">{r.seller_sku}</td>
          <td className="wrap-cell"><List items={r.now} /></td><td className="wrap-cell"><List items={r.will_be} /></td>
          <td className={`font-semibold ${LABEL[r.status][1]}`}>{LABEL[r.status][0]}{r.sales.length > 0 && <div className="font-normal text-red-600">{r.sales.length} sale entr{r.sales.length === 1 ? "y" : "ies"} already recorded</div>}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

export const AsinRepairBanner = ({ onDone }) => {
  const { data, reload } = useLoad(() => api.get("/asins-repair/2cut").then((r) => r.data));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!data?.pending) return null;
  const sold = data.rows.filter((r) => r.sales.length);
  const apply = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/asins-repair/2cut/apply")).data;
      toast.success(`${r.fixed} ASINs corrected, ${r.added} added${r.created_products.length ? `, plates created: ${r.created_products.join(", ")}` : ""}`);
      setOpen(false); reload(); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="asin-repair-banner">
        <span>The 37 2Cut ASINs from the sheet upload need correcting: some point to the wrong products (SKU clash) or are missing. Review the correction before it is applied.</span>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="asin-repair-review-btn"><Wrench className="h-4 w-4" /> Review fix</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl" data-testid="asin-repair-dialog">
          <DialogHeader>
            <DialogTitle>Correct 2Cut ASIN contents</DialogTitle>
            <DialogDescription>Products are matched by name, not SKU. Only these 37 ASINs and missing 2Cut plates are touched; every other ASIN, product, sale and stock record stays as it is.</DialogDescription>
          </DialogHeader>
          <div className="text-xs" data-testid="asin-repair-plates"><b>2Cut plates:</b> {data.plates.map((p) => `${p.name} → ${p.status === "exists" ? p.sku : "new (next free SKU, stock 0)"}`).join(" · ")}</div>
          {data.blockers.length > 0 && <div className="rounded bg-red-50 p-2 text-xs text-red-700" data-testid="asin-repair-blockers">{data.blockers.join(" · ")}</div>}
          {sold.length > 0 && <div className="rounded bg-red-50 p-2 text-xs text-red-700" data-testid="asin-repair-sales-warning">{sold.length} of these ASINs already have sales recorded with the old (wrong) products. Those past entries are NOT changed — delete and re-enter them in ASIN Sales if their stock should move to the 2Cut plates.</div>}
          <RepairTable rows={data.rows} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="asin-repair-cancel-btn">Cancel</Button>
            <Button onClick={apply} disabled={busy || data.blockers.length > 0} data-testid="asin-repair-apply-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply correction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
